import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Settings, DollarSign, Clock, Save, Loader2, CheckCircle2,
  Webhook, ScrollText, Download, Filter, RefreshCw, CalendarDays, Plus, Pencil,
  Trash2, Sparkles, Lock
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';
import { getParkingPrice } from '../lib/parkingPrices';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const TABS = [
  { id: 'tariff', label: 'Tarifas', icon: DollarSign },
  { id: 'events', label: 'Eventos', icon: CalendarDays },
  { id: 'integrations', label: 'Integraciones', icon: Webhook },
  { id: 'logs', label: 'Registros Logs', icon: ScrollText },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    return TABS.some(x => x.id === t) ? t : 'tariff';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && TABS.some(x => x.id === t) && t !== activeTab) {
      setActiveTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const switchTab = (id) => {
    setActiveTab(id);
    const params = new URLSearchParams(location.search);
    params.set('tab', id);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BackButton to="/dashboard" testId="settings-back-btn" />
              <div className="border-l border-slate-700 pl-4">
                <h1 className="text-lg font-bold text-white">Configuracion</h1>
                <p className="text-xs sm:text-sm text-slate-400">Tarifas, integraciones y registros</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {isAdmin && (
                <Link to="/permissions" className="hidden sm:block">
                  <Button variant="outline" size="sm" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200" data-testid="settings-permissions-link">
                    <Lock className="w-4 h-4 mr-2" />Permisos
                  </Button>
                </Link>
              )}
              <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto" />
            </div>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex gap-1 sm:gap-2 overflow-x-auto -mb-px">
            {TABS.map(t => {
              const Icon = t.icon;
              const isLogs = t.id === 'logs';
              if (isLogs && !isAdmin) return null;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => switchTab(t.id)}
                  data-testid={`settings-tab-${t.id}`}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                    active ? 'text-white border-emerald-500' : 'text-slate-400 border-transparent hover:text-slate-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />{t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-5xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
        {activeTab === 'tariff' && <TariffTab />}
        {activeTab === 'events' && <EventTypesTab isAdmin={isAdmin} />}
        {activeTab === 'integrations' && <IntegrationsTab />}
        {activeTab === 'logs' && isAdmin && <AuditLogsTab />}
      </main>

      <AppFooter />
    </div>
  );
}

const ADDON_MODES = ['disabled', 'optional', 'required'];

function normalizeTariffFromApi(data) {
  const raw = data || {};
  const mode = ADDON_MODES.includes(raw.event_addon_mode) ? raw.event_addon_mode : 'disabled';
  return {
    price_per_hour: Number(raw.price_per_hour) || 500,
    price_per_minute: Number(raw.price_per_minute) || 10,
    currency: raw.currency || 'ARS',
    billing_mode: raw.billing_mode === 'flat_rate' ? 'flat_rate' : 'per_minute',
    flat_rate_price: Number(raw.flat_rate_price) || 0,
    event_addon_mode: mode,
    event_addon_default_id: raw.event_addon_default_id ? String(raw.event_addon_default_id) : '',
    event_addon_label: raw.event_addon_label ? String(raw.event_addon_label) : '',
  };
}

/* ================= TARIFAS ================= */
function TariffTab() {
  const [tariff, setTariff] = useState({
    price_per_hour: 500, price_per_minute: 10, currency: 'ARS',
    billing_mode: 'per_minute', flat_rate_price: 0,
    event_addon_mode: 'disabled', event_addon_default_id: '', event_addon_label: '',
  });
  const [eventTypes, setEventTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    setLoadError(null);
    try {
      const [t, ev] = await Promise.all([
        axios.get(`${API}/tariff`, { withCredentials: true }),
        axios.get(`${API}/event-types?only_active=true`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTariff(normalizeTariffFromApi(t.data));
      setEventTypes(Array.isArray(ev.data) ? ev.data : []);
    } catch (err) {
      console.error(err);
      setLoadError(err.response?.data?.detail || 'No se pudo cargar la configuracion');
      toast.error('Error al cargar tarifas');
    } finally {
      setIsLoading(false);
    }
  };

  const buildTariffPayload = () => ({
    price_per_hour: Number(tariff.price_per_hour) || 0,
    price_per_minute: Number(tariff.price_per_minute) || 0,
    currency: tariff.currency || 'ARS',
    billing_mode: tariff.billing_mode === 'flat_rate' ? 'flat_rate' : 'per_minute',
    flat_rate_price: Number(tariff.flat_rate_price) || 0,
    event_addon_mode: ADDON_MODES.includes(tariff.event_addon_mode) ? tariff.event_addon_mode : 'disabled',
    event_addon_default_id: tariff.event_addon_default_id || '',
    event_addon_label: (tariff.event_addon_label || '').trim(),
  });

  const handleSave = async () => {
    const payload = buildTariffPayload();
    if (payload.event_addon_mode === 'required' && !payload.event_addon_default_id) {
      toast.error('El modo "obligatorio" requiere un evento por defecto');
      return;
    }
    if (
      payload.event_addon_default_id
      && !eventTypes.some((et) => et.id === payload.event_addon_default_id)
    ) {
      toast.error('El evento por defecto ya no existe. Elegi otro o dejalo vacio.');
      return;
    }
    setIsSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/tariff`, payload, { withCredentials: true });
      setTariff(normalizeTariffFromApi(payload));
      setSaved(true);
      toast.success('Tarifa guardada');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar tarifa');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(a);
  const isFlatRate = tariff.billing_mode === 'flat_rate';
  const examples = [
    { duration: 30, label: '30 min' }, { duration: 60, label: '1 hora' },
    { duration: 120, label: '2 horas' }, { duration: 180, label: '3 horas' },
    { duration: 300, label: '5 horas' }, { duration: 480, label: '8 horas' }
  ];
  const calculateAmount = (m) => isFlatRate ? tariff.flat_rate_price : Math.max(5, Math.ceil(m / 5) * 5) * tariff.price_per_minute;

  if (isLoading) return <div className="text-center py-12 text-slate-400">Cargando...</div>;

  if (loadError) {
    return (
      <div className="text-center py-12 space-y-4">
        <p className="text-red-400">{loadError}</p>
        <Button onClick={fetchAll} variant="outline" className="border-slate-600 text-slate-300">
          <RefreshCw className="w-4 h-4 mr-2" />Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-emerald-700/50 bg-slate-800" data-testid="parking-fixed-prices">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white"><DollarSign className="h-5 w-5 text-emerald-400" />Tarifas de estacionamiento</CardTitle>
          <CardDescription className="text-slate-400">Valores centralizados aplicados en entrada, salida, terminal y escáner.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-amber-500/40 bg-amber-900/20 p-4">
            <p className="text-sm text-amber-200">Moto</p>
            <p className="text-3xl font-bold text-white">{formatCurrency(getParkingPrice('moto', tariff))}</p>
          </div>
          <div className="rounded-xl border border-blue-500/40 bg-blue-900/20 p-4">
            <p className="text-sm text-blue-200">Auto</p>
            <p className="text-3xl font-bold text-white">{formatCurrency(getParkingPrice('auto', tariff))}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="hidden bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Settings className="w-5 h-5" />Modo de Facturacion</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div onClick={() => setTariff({ ...tariff, billing_mode: 'per_minute' })}
              className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${!isFlatRate ? 'bg-emerald-900/30 border-emerald-500' : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'}`}
              data-testid="mode-per-minute">
              <div className="flex items-center gap-2 mb-2"><Clock className="w-5 h-5 text-emerald-400" /><span className="text-white font-bold">Por Minuto</span></div>
              <p className="text-slate-400 text-sm">Cobra segun el tiempo estacionado (redondeado a multiplos de 5 min)</p>
            </div>
            <div onClick={() => setTariff({ ...tariff, billing_mode: 'flat_rate' })}
              className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${isFlatRate ? 'bg-blue-900/30 border-blue-500' : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'}`}
              data-testid="mode-flat-rate">
              <div className="flex items-center gap-2 mb-2"><DollarSign className="w-5 h-5 text-blue-400" /><span className="text-white font-bold">Tarifa Unica</span></div>
              <p className="text-slate-400 text-sm">Un precio fijo sin importar cuanto tiempo este (ideal para todo el dia)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="hidden bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><DollarSign className="w-5 h-5" />{isFlatRate ? 'Tarifa Unica' : 'Tarifas por Tiempo'}</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {isFlatRate ? (
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2"><DollarSign className="w-4 h-4" />Precio Unico (por estadia)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <Input type="number" value={tariff.flat_rate_price}
                  onChange={(e) => setTariff({ ...tariff, flat_rate_price: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-700 border-slate-600 text-white text-3xl font-bold pl-8 h-16" min="0" step="50" data-testid="flat-rate-input" />
              </div>
              <p className="text-slate-500 text-sm">Se cobra este monto fijo sin importar la duracion</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2"><Clock className="w-4 h-4" />Precio por Hora (referencia)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input type="number" value={tariff.price_per_hour}
                    onChange={(e) => setTariff({ ...tariff, price_per_hour: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600 text-white text-xl font-bold pl-8" min="0" step="10" data-testid="price-per-hour-input" />
                </div>
                <p className="text-slate-500 text-sm">Referencia para calculos</p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2"><Clock className="w-4 h-4" />Precio por Minuto</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input type="number" value={tariff.price_per_minute}
                    onChange={(e) => setTariff({ ...tariff, price_per_minute: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600 text-white text-xl font-bold pl-8" min="0" step="1" data-testid="price-per-minute-input" />
                </div>
                <p className="text-slate-500 text-sm">Redondeado a multiplos de 5 min</p>
              </div>
            </div>
          )}
          <Button onClick={handleSave} disabled={isSaving} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-lg" data-testid="save-tariff-btn">
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : saved ? <CheckCircle2 className="w-5 h-5 mr-2" /> : <Save className="w-5 h-5 mr-2" />}
            {saved ? 'Guardado!' : 'Guardar Cambios'}
          </Button>
        </CardContent>
      </Card>

      {/* Evento adicional en entrada (admin-controlled) */}
      <Card className="bg-slate-800 border-slate-700" data-testid="event-addon-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-amber-400" />
            Evento adicional en entrada
          </CardTitle>
          <CardDescription className="text-slate-400">
            Permite cobrar un evento ADEMAS del estacionamiento al registrar la entrada.
            Solo admin/super_admin pueden cambiar esta configuracion.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            {[
              { id: 'disabled', label: 'Desactivado', desc: 'No se ofrece evento en entrada', tone: 'slate' },
              { id: 'optional', label: 'Opcional', desc: 'El operador puede activarlo al ingresar', tone: 'emerald' },
              { id: 'required', label: 'Obligatorio', desc: 'Toda entrada exige un tipo de evento', tone: 'amber' },
            ].map((opt) => {
              const active = tariff.event_addon_mode === opt.id;
              const toneActive = {
                slate: 'bg-slate-700/50 border-slate-400',
                emerald: 'bg-emerald-900/30 border-emerald-500',
                amber: 'bg-amber-900/30 border-amber-500',
              }[opt.tone];
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setTariff({ ...tariff, event_addon_mode: opt.id })}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${active ? toneActive : 'bg-slate-700/30 border-slate-600 hover:border-slate-500'}`}
                  data-testid={`addon-mode-${opt.id}`}
                >
                  <div className="text-sm font-bold text-white">{opt.label}</div>
                  <div className="text-[11px] text-slate-400 mt-1 leading-snug">{opt.desc}</div>
                </button>
              );
            })}
          </div>

          {tariff.event_addon_mode !== 'disabled' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">
                  Evento por defecto {tariff.event_addon_mode === 'required' && <span className="text-amber-400">*</span>}
                </Label>
                <Select
                  value={tariff.event_addon_default_id || '__none__'}
                  onValueChange={(v) => setTariff({ ...tariff, event_addon_default_id: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white event-select-trigger" data-testid="addon-default-select">
                    <SelectValue placeholder="Sin valor por defecto" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-72 event-select-content">
                    <SelectItem
                      value="__none__"
                      className="text-slate-100 focus:bg-slate-700 focus:text-white data-[state=checked]:bg-emerald-700/40 data-[state=checked]:text-white"
                    >
                      Sin valor por defecto
                    </SelectItem>
                    {eventTypes.map(et => (
                      <SelectItem
                        key={et.id}
                        value={et.id}
                        className="text-slate-100 focus:bg-slate-700 focus:text-white data-[state=checked]:bg-emerald-700/40 data-[state=checked]:text-white"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: et.color || '#10b981' }} />
                          {et.name}{' '}
                          <span className="text-emerald-400 ml-2 font-mono">
                            {formatCurrency(Number(et.price) || 0)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">
                  {tariff.event_addon_mode === 'required'
                    ? 'En modo obligatorio se preselecciona automaticamente.'
                    : 'Si lo dejas vacio, el operador elige al activar el toggle.'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Texto del toggle (opcional)</Label>
                <Input
                  value={tariff.event_addon_label || ''}
                  onChange={(e) => setTariff({ ...tariff, event_addon_label: e.target.value })}
                  placeholder='Ej: "Entrada al partido"'
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="addon-label-input"
                />
                <p className="text-[11px] text-slate-500">
                  Se muestra al operador en la pantalla de entrada. Por defecto: "Activar evento".
                </p>
              </div>
            </div>
          )}

          <Button onClick={handleSave} disabled={isSaving} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="save-addon-btn">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {saved ? 'Guardado' : 'Guardar configuracion'}
          </Button>
        </CardContent>
      </Card>

      <Card className="hidden bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Ejemplos de Precios</CardTitle>
          <CardDescription className="text-slate-400">{isFlatRate ? 'Precio fijo sin importar la duracion' : 'Cuanto se cobraria segun la duracion'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {examples.map((ex) => (
              <div key={ex.duration} className="bg-slate-700/50 rounded-lg p-3 sm:p-4 text-center">
                <p className="text-slate-400 text-xs sm:text-sm mb-1">{ex.label}</p>
                <p className="text-lg sm:text-2xl font-bold text-emerald-400 break-words">{formatCurrency(calculateAmount(ex.duration))}</p>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-4 text-center">{isFlatRate ? '* Todos pagan lo mismo, sin importar la duracion' : '* Minimo 5 minutos. Socios no pagan.'}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============== EVENTOS (TIPOS DE EVENTO) ============== */
const EVENT_COLOR_PRESETS = [
  '#10b981', '#3b82f6', '#a855f7', '#f59e0b', '#f97316',
  '#ec4899', '#06b6d4', '#84cc16', '#ef4444', '#64748b',
];

function EventTypesTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState(null); // null = no edit, {} = creating, {...} = editing
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fmtMoney = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/event-types`, { withCredentials: true });
      setItems(data);
    } catch (err) {
      toast.error('Error cargando tipos de evento');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/event-types/seed-defaults`, {}, { withCredentials: true });
      toast.success(data.message || 'Listo');
      fetchItems();
    } catch { toast.error('No se pudo cargar el set inicial'); }
    finally { setSeeding(false); }
  };

  const startCreate = () => setEditing({
    name: '', price: 0, active: true, sort_order: (items.length + 1) * 10,
    color: EVENT_COLOR_PRESETS[items.length % EVENT_COLOR_PRESETS.length], description: '',
  });
  const startEdit = (it) => setEditing({ ...it });
  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!(editing.name || '').trim()) { toast.error('Falta el nombre'); return; }
    if (editing.price == null || editing.price < 0) { toast.error('Precio invalido'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        price: parseFloat(editing.price) || 0,
        active: !!editing.active,
        sort_order: parseInt(editing.sort_order, 10) || 100,
        color: (editing.color || '').trim() || null,
        description: (editing.description || '').trim() || null,
      };
      if (editing.id) {
        await axios.put(`${API}/event-types/${editing.id}`, payload, { withCredentials: true });
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API}/event-types`, payload, { withCredentials: true });
        toast.success('Evento creado');
      }
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/event-types/${id}`, { withCredentials: true });
      toast.success('Eliminado');
      setConfirmDelete(null);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
  };

  const totalActive = items.filter(i => i.active).length;
  const avgPrice = items.length ? Math.round(items.reduce((s, i) => s + (i.price || 0), 0) / items.length) : 0;

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-white flex items-center gap-2"><CalendarDays className="w-5 h-5 text-emerald-400" />Tipos de Evento</CardTitle>
            <CardDescription className="text-slate-400">
              Catalogo de eventos con precio fijo. Se aplica al cobrar entradas como evento.
            </CardDescription>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={seedDefaults} disabled={seeding} variant="outline" size="sm" className="border-slate-600 text-slate-200 bg-slate-700/40 hover:bg-slate-700" data-testid="event-seed-btn">
                {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Cargar set inicial
              </Button>
              <Button onClick={startCreate} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="event-new-btn">
                <Plus className="w-4 h-4 mr-2" />Nuevo evento
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Stats summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg p-3 bg-slate-700/40 border border-slate-700">
              <p className="text-xs text-slate-400 uppercase">Tipos cargados</p>
              <p className="text-2xl font-bold text-white">{items.length}</p>
            </div>
            <div className="rounded-lg p-3 bg-slate-700/40 border border-slate-700">
              <p className="text-xs text-slate-400 uppercase">Activos</p>
              <p className="text-2xl font-bold text-emerald-400">{totalActive}</p>
            </div>
            <div className="rounded-lg p-3 bg-slate-700/40 border border-slate-700 col-span-2 sm:col-span-1">
              <p className="text-xs text-slate-400 uppercase">Precio promedio</p>
              <p className="text-2xl font-bold text-white">{fmtMoney(avgPrice)}</p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 sm:py-14">
              <CalendarDays className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-slate-500 mb-3" />
              <p className="text-slate-400 mb-4">No hay tipos de evento cargados todavia.</p>
              {isAdmin && (
                <Button onClick={seedDefaults} disabled={seeding} className="bg-emerald-600 hover:bg-emerald-700">
                  {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Cargar set inicial sugerido
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {items.map(it => (
                  <div key={it.id} className="p-3 rounded-lg bg-slate-700/30 border border-slate-700" data-testid={`event-row-${it.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: it.color || '#10b981' }} />
                      <span className="text-white font-bold flex-1 truncate">{it.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${it.active ? 'bg-emerald-900/40 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>{it.active ? 'Activo' : 'Inactivo'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-emerald-400 text-lg font-bold">{fmtMoney(it.price)}</span>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(it)} className="text-blue-400 h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(it)} className="text-red-400 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      )}
                    </div>
                    {it.description && <p className="text-xs text-slate-400 mt-1">{it.description}</p>}
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-700">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/60">
                    <tr className="text-left text-slate-400">
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2">Nombre</th>
                      <th className="px-3 py-2">Precio</th>
                      <th className="px-3 py-2">Orden</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Descripcion</th>
                      {isAdmin && <th className="px-3 py-2 text-right">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.id} className="border-t border-slate-700/50 text-slate-200 hover:bg-slate-700/20" data-testid={`event-row-${it.id}`}>
                        <td className="px-3 py-2"><span className="inline-block w-3 h-3 rounded-full" style={{ background: it.color || '#10b981' }} /></td>
                        <td className="px-3 py-2 font-medium text-white">{it.name}</td>
                        <td className="px-3 py-2 text-emerald-400 font-bold">{fmtMoney(it.price)}</td>
                        <td className="px-3 py-2 text-slate-400">{it.sort_order}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded ${it.active ? 'bg-emerald-900/40 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>{it.active ? 'Activo' : 'Inactivo'}</span>
                        </td>
                        <td className="px-3 py-2 text-slate-300 text-xs max-w-xs truncate">{it.description || '-'}</td>
                        {isAdmin && (
                          <td className="px-3 py-2 text-right space-x-1">
                            <Button size="sm" variant="ghost" onClick={() => startEdit(it)} className="text-blue-400 hover:text-blue-300 h-7 w-7 p-0" data-testid={`event-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(it)} className="text-red-400 hover:text-red-300 h-7 w-7 p-0" data-testid={`event-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit / create modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={cancelEdit}>
          <Card className="bg-slate-800 border-slate-700 w-full sm:max-w-lg rounded-t-xl sm:rounded-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-emerald-400" />
                {editing.id ? 'Editar tipo de evento' : 'Nuevo tipo de evento'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300 text-sm">Nombre</Label>
                <Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="Ej: Liga de Futbol" data-testid="event-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-sm">Precio (ARS)</Label>
                  <Input type="number" min="0" step="100" value={editing.price ?? 0}
                    onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="event-form-price" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Orden</Label>
                  <Input type="number" value={editing.sort_order ?? 100}
                    onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                    className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="event-form-order" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Color</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {EVENT_COLOR_PRESETS.map(c => (
                    <button key={c} type="button" onClick={() => setEditing({ ...editing, color: c })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${editing.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                      style={{ background: c }} aria-label={c} />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Descripcion (opcional)</Label>
                <Input value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="Detalle visible para el operador" data-testid="event-form-desc" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="event-active" checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="rounded bg-slate-700 border-slate-600" data-testid="event-form-active" />
                <Label htmlFor="event-active" className="text-slate-300 text-sm cursor-pointer">Activo (disponible para cobro)</Label>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={cancelEdit} className="flex-1 border-slate-600 text-slate-300">Cancelar</Button>
                <Button onClick={save} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="event-form-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <Card className="bg-slate-800 border-slate-700 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white text-lg">Eliminar evento</CardTitle>
              <CardDescription className="text-slate-400">
                Vas a eliminar <strong className="text-white">{confirmDelete.name}</strong>. Esta accion no se puede deshacer.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(null)} className="flex-1 border-slate-600 text-slate-300">Cancelar</Button>
                <Button onClick={() => remove(confirmDelete.id)} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="event-confirm-delete">
                  <Trash2 className="w-4 h-4 mr-2" />Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ============== INTEGRACIONES (WEBHOOK) ============== */
function IntegrationsTab() {
  const [webhook, setWebhook] = useState({ webhook_url: '', webhook_events: ['entry', 'exit', 'member_created', 'payment'] });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/webhook-settings`, { withCredentials: true })
      .then(({ data }) => setWebhook(data)).catch(() => {});
  }, []);

  const save = async () => {
    setIsSaving(true);
    try { await axios.put(`${API}/webhook-settings`, webhook, { withCredentials: true }); toast.success('Webhook guardado'); }
    catch { toast.error('Error'); }
    finally { setIsSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2"><Webhook className="w-5 h-5" />Webhook (n8n / Make / Zapier)</CardTitle>
          <CardDescription className="text-slate-400">Conecta ParkingVision con sistemas externos via webhook HTTP</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300">URL del Webhook</Label>
            <Input value={webhook.webhook_url} onChange={(e) => setWebhook({ ...webhook, webhook_url: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white mt-1 font-mono text-sm break-all"
              placeholder="https://n8n.tudominio.com/webhook/parking" data-testid="webhook-url" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Eventos que envia:</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {['entry', 'exit', 'member_created', 'payment'].map(evt => (
                <label key={evt} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={webhook.webhook_events.includes(evt)}
                    onChange={(e) => {
                      const events = e.target.checked ? [...webhook.webhook_events, evt] : webhook.webhook_events.filter(x => x !== evt);
                      setWebhook({ ...webhook, webhook_events: events });
                    }} className="rounded bg-slate-700 border-slate-600" data-testid={`webhook-evt-${evt}`} />
                  {evt === 'entry' ? 'Entrada vehiculo' : evt === 'exit' ? 'Salida vehiculo' : evt === 'member_created' ? 'Socio creado' : 'Pago registrado'}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={save} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="save-webhook-btn">
            <Save className="w-4 h-4 mr-2" />Guardar Webhook
          </Button>
          <div className="mt-3 p-3 bg-slate-700/50 rounded-lg overflow-x-auto">
            <p className="text-slate-400 text-xs font-bold mb-2">Variables que envia cada evento:</p>
            <pre className="text-slate-300 text-xs font-mono whitespace-pre-wrap">{`// entry / exit
{
  "event": "entry" | "exit",
  "plate": "AB123CD",
  "vehicle_type": "auto" | "moto",
  "is_member": true | false,
  "member_name": "Juan Perez",
  "entry_time": "2026-04-22T10:30:00Z",
  "exit_time": "2026-04-22T12:00:00Z",
  "duration_minutes": 90,
  "amount_charged": 900.00,
  "timestamp": "2026-04-22T12:00:00Z"
}

// member_created
{ "event": "member_created", "nombre": "Juan", "apellido": "Perez",
  "dni": "12345678", "plate": "AB123CD", "vehicle_type": "auto",
  "is_free": false, "timestamp": "..." }

// payment
{ "event": "payment", "member_name": "Juan Perez", "amount": 5000,
  "period": "2026-04", "method": "efectivo", "timestamp": "..." }`}</pre>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-900/20 border-blue-700/50">
        <CardContent className="p-4 text-blue-200 text-sm">
          <strong>Edge Connector (proximamente):</strong> integra camaras IP RTSP/ONVIF para registrar entradas y salidas automaticamente. Documentacion completa en <code className="bg-slate-900/60 px-1 rounded">EDGE_CONNECTOR_GUIDE.md</code> del repositorio.
        </CardContent>
      </Card>
    </div>
  );
}

/* ============== AUDIT LOGS ============== */
function AuditLogsTab() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ user_email: '', action: 'all', date_from: '', date_to: '' });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.action && filters.action !== 'all') params.action = filters.action;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await axios.get(`${API}/audit`, { params, withCredentials: true });
      setLogs(data);
    } catch (err) {
      toast.error('Error cargando registros');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/audit/actions`, { withCredentials: true }).then(({ data }) => setActions(data)).catch(() => {});
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.action && filters.action !== 'all') params.append('action', filters.action);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      const res = await axios.get(`${API}/audit/export/excel?${params.toString()}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `historial_logs_${Date.now()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Excel descargado');
    } catch { toast.error('Error exportando'); }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; }
  };

  const actionColor = (a) => {
    if (a.includes('delete') || a.includes('reject')) return 'bg-red-900/40 text-red-300';
    if (a.includes('create') || a.includes('approve')) return 'bg-emerald-900/40 text-emerald-300';
    if (a.includes('update') || a.includes('edit') || a.includes('toggle')) return 'bg-amber-900/40 text-amber-300';
    return 'bg-slate-700/60 text-slate-300';
  };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2"><ScrollText className="w-5 h-5" />Registros / Logs del sistema</CardTitle>
          <CardDescription className="text-slate-400">Historial inmutable de acciones criticas. Solo lectura para administradores.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <Label className="text-slate-400 text-xs">Email usuario</Label>
              <Input value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
                placeholder="busqueda parcial" className="bg-slate-700 border-slate-600 text-white text-sm" data-testid="audit-filter-email" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Accion</Label>
              <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded px-2 py-2 text-sm" data-testid="audit-filter-action">
                <option value="all">Todas</option>
                {actions.map(a => <option key={a.code} value={a.code}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Desde</Label>
              <Input type="date" value={filters.date_from?.slice(0, 10) || ''}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value ? `${e.target.value}T00:00:00Z` : '' })}
                className="bg-slate-700 border-slate-600 text-white text-sm" data-testid="audit-filter-from" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Hasta</Label>
              <Input type="date" value={filters.date_to?.slice(0, 10) || ''}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value ? `${e.target.value}T23:59:59Z` : '' })}
                className="bg-slate-700 border-slate-600 text-white text-sm" data-testid="audit-filter-to" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={fetchLogs} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="audit-apply-btn">
              <Filter className="w-4 h-4 mr-2" />Aplicar filtros
            </Button>
            <Button onClick={fetchLogs} variant="outline" className="border-slate-600 text-slate-200 bg-slate-700/40 hover:bg-slate-700" data-testid="audit-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refrescar
            </Button>
            <Button onClick={exportExcel} className="bg-blue-600 hover:bg-blue-700" data-testid="audit-export-btn">
              <Download className="w-4 h-4 mr-2" />Exportar Excel
            </Button>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-700">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60">
                <tr className="text-left text-slate-400">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Usuario</th>
                  <th className="px-3 py-2">Rol</th>
                  <th className="px-3 py-2">Accion</th>
                  <th className="px-3 py-2">Detalles</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && !loading && (
                  <tr><td colSpan="5" className="text-center py-8 text-slate-500">Sin registros</td></tr>
                )}
                {logs.map(l => (
                  <tr key={l.id} className="border-t border-slate-700/50 text-slate-200" data-testid={`audit-row-${l.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{fmt(l.timestamp)}</td>
                    <td className="px-3 py-2"><div className="font-medium">{l.user_name || '-'}</div><div className="text-xs text-slate-400">{l.user_email}</div></td>
                    <td className="px-3 py-2 text-xs uppercase text-slate-400">{l.user_role}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-1 rounded text-xs font-semibold ${actionColor(l.action)}`}>{l.action_label}</span></td>
                    <td className="px-3 py-2 text-slate-300 break-words max-w-md">{l.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {logs.length === 0 && !loading && <div className="text-center py-8 text-slate-500">Sin registros</div>}
            {logs.map(l => (
              <div key={l.id} className="p-3 rounded-lg bg-slate-900/50 border border-slate-700">
                <div className="flex justify-between items-start gap-2 mb-2">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${actionColor(l.action)}`}>{l.action_label}</span>
                  <span className="text-xs text-slate-400">{fmt(l.timestamp)}</span>
                </div>
                <div className="text-sm text-white font-medium">{l.user_name || l.user_email}</div>
                <div className="text-xs text-slate-400 mb-2">{l.user_email} · {l.user_role}</div>
                <div className="text-sm text-slate-200 break-words">{l.details}</div>
              </div>
            ))}
          </div>

          <p className="text-slate-500 text-xs mt-3">Mostrando hasta 200 registros mas recientes. Para historial completo exporta a Excel.</p>
        </CardContent>
      </Card>
    </div>
  );
}
